import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Bell, TriangleAlert } from 'lucide-react';
import { useRevit } from '../../contexts/RevitContext';
import './Notifications.css';

const SEEN_KEY = 'revtrack-seen-update-ids';
const SEEN_ALERTS_KEY = 'revtrack-seen-missing-offer-alerts';
const SEEN_STOPPED_KEY = 'revtrack-seen-tracking-stopped-alerts';
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const UPDATES = [
    {
        id: 1,
        title: 'Revtrack now has an MCP server (pro feature)',
        description: 'You can now connect your LLM tool to Revtrack to ask questions about your data. To connect it, go to the API & MCP Server section and follow the instructions. Reach out to us if you run into issues.',
        date: 'October 8th, 2026',
        isNew: true
    },
    {
        id: 1,
        title: 'New data available in the Audience tab',
        description: 'See the countries, devices and traffic sources that are driving your clicks & sales.',
        date: 'October 1st, 2026',
        isNew: true
    },
    {
        id: 2,
        title: 'Track conversions from QR codes',
        description: 'Create trackable QR codes for your YouTube videos and other sources from the new QR Code Generator in the Links section.',
        date: 'August 29th, 2026',
        isNew: false
    },
];

function readSeenIds() {
    try {
        const raw = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        return Array.isArray(raw) ? raw : [];
    } catch {
        return [];
    }
}

function alertKey(alert) {
    return `${alert.offerName}|${alert.alertedAt || ''}`;
}

function readSeenAlertKeys(username) {
    if (!username) return [];
    try {
        const raw = JSON.parse(localStorage.getItem(SEEN_ALERTS_KEY) || '{}');
        const list = raw[username];
        return Array.isArray(list) ? list : [];
    } catch {
        return [];
    }
}

function writeSeenAlertKeys(username, keys) {
    if (!username) return;
    try {
        const raw = JSON.parse(localStorage.getItem(SEEN_ALERTS_KEY) || '{}');
        raw[username] = keys;
        localStorage.setItem(SEEN_ALERTS_KEY, JSON.stringify(raw));
    } catch {
        // The badge still clears for this session.
    }
}

function stoppedKey(alert) {
    return `${alert.offerId}|${alert.lastEventAt || ''}`;
}

function readSeenStoppedKeys(username) {
    if (!username) return [];
    try {
        const raw = JSON.parse(localStorage.getItem(SEEN_STOPPED_KEY) || '{}');
        const list = raw[username];
        return Array.isArray(list) ? list : [];
    } catch {
        return [];
    }
}

function writeSeenStoppedKeys(username, keys) {
    if (!username) return;
    try {
        const raw = JSON.parse(localStorage.getItem(SEEN_STOPPED_KEY) || '{}');
        raw[username] = keys;
        localStorage.setItem(SEEN_STOPPED_KEY, JSON.stringify(raw));
    } catch {
        // The badge still clears for this session.
    }
}

function formatAlertDate(value) {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

function formatLastClick(value) {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!match) return formatAlertDate(value);
    const [, year, month, day, hour, minute] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    const label = date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
    return `${label}, ${hour}:${minute} UTC`;
}

const Notifications = () => {
    const { username } = useRevit();
    const { getToken } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [seenIds, setSeenIds] = useState(readSeenIds);
    const [seenAlertKeys, setSeenAlertKeys] = useState([]);
    const [seenStoppedKeys, setSeenStoppedKeys] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [stoppedAlerts, setStoppedAlerts] = useState([]);
    const [deletingName, setDeletingName] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const rootRef = useRef(null);
    const loadIdRef = useRef(0);

    const unseenCount = UPDATES.filter((update) => update.isNew && !seenIds.includes(update.id)).length;
    const seenAlertKeySet = new Set(seenAlertKeys);
    const seenStoppedKeySet = new Set(seenStoppedKeys);
    const unseenMissingCount = alerts.filter((alert) => !seenAlertKeySet.has(alertKey(alert))).length;
    const unseenStoppedCount = stoppedAlerts.filter((alert) => !seenStoppedKeySet.has(stoppedKey(alert))).length;
    const unseenAlertCount = unseenMissingCount + unseenStoppedCount;
    const hasAlerts = alerts.length > 0 || stoppedAlerts.length > 0;

    const loadAlerts = useCallback(async () => {
        if (!username) return;
        const loadId = ++loadIdRef.current;
        try {
            const token = await getToken();
            const headers = { Authorization: `Bearer ${token}` };
            const [missingResponse, stoppedResponse] = await Promise.all([
                fetch(`https://${SERVER_URL}/api/missing-offer-alerts/${encodeURIComponent(username)}`, { headers }),
                fetch(`https://${SERVER_URL}/api/tracking-stopped-alerts/${encodeURIComponent(username)}`, { headers }),
            ]);
            if (loadId !== loadIdRef.current) return;
            if (missingResponse.ok) {
                const data = await missingResponse.json();
                setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
            }
            if (stoppedResponse.ok) {
                const data = await stoppedResponse.json();
                setStoppedAlerts(Array.isArray(data.alerts) ? data.alerts : []);
            }
        } catch {
            // Keep the last list if the request fails.
        }
    }, [username, getToken]);

    useEffect(() => {
        setSeenAlertKeys(readSeenAlertKeys(username));
        setSeenStoppedKeys(readSeenStoppedKeys(username));
    }, [username]);

    useEffect(() => {
        if (!isOpen || !username || alerts.length === 0) return;
        setSeenAlertKeys((current) => {
            const next = new Set(current);
            let changed = false;
            for (const alert of alerts) {
                const key = alertKey(alert);
                if (!next.has(key)) {
                    next.add(key);
                    changed = true;
                }
            }
            if (!changed) return current;
            const keys = [...next];
            writeSeenAlertKeys(username, keys);
            return keys;
        });
    }, [isOpen, alerts, username]);

    useEffect(() => {
        if (!isOpen || !username || stoppedAlerts.length === 0) return;
        setSeenStoppedKeys((current) => {
            const next = new Set(current);
            let changed = false;
            for (const alert of stoppedAlerts) {
                const key = stoppedKey(alert);
                if (!next.has(key)) {
                    next.add(key);
                    changed = true;
                }
            }
            if (!changed) return current;
            const keys = [...next];
            writeSeenStoppedKeys(username, keys);
            return keys;
        });
    }, [isOpen, stoppedAlerts, username]);

    useEffect(() => {
        let cancelled = false;
        const start = () => {
            if (!cancelled) loadAlerts();
        };
        // Let the dashboard paint and start its own requests before this one.
        const idleId = typeof window.requestIdleCallback === 'function'
            ? window.requestIdleCallback(start, { timeout: 2500 })
            : null;
        const timeoutId = idleId == null ? window.setTimeout(start, 1) : null;
        return () => {
            cancelled = true;
            if (idleId != null) window.cancelIdleCallback(idleId);
            if (timeoutId != null) window.clearTimeout(timeoutId);
        };
    }, [loadAlerts]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [isOpen]);

    const toggle = () => {
        setIsOpen((open) => {
            const next = !open;
            if (next) {
                const ids = UPDATES.map((update) => update.id);
                setSeenIds(ids);
                try {
                    localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
                } catch {
                    // Ignore storage failures; the badge still clears for this session.
                }
                loadAlerts();
            }
            return next;
        });
    };

    const deleteAlert = async (offerName) => {
        if (!username || deletingName) return;
        loadIdRef.current += 1;
        setDeletingName(offerName);
        setDeleteError('');
        try {
            const token = await getToken();
            const response = await fetch(`https://${SERVER_URL}/api/missing-offer-alerts`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ username, offerName }),
            });
            if (!response.ok) throw new Error('Failed to delete alert');
            setAlerts((current) => current.filter((alert) => alert.offerName !== offerName));
        } catch {
            setDeleteError(offerName);
        } finally {
            setDeletingName(null);
        }
    };

    return (
        <div className="notifications-container" ref={rootRef}>
            <button
                type="button"
                className={`notifications-toggle ${isOpen ? 'open' : ''} ${unseenAlertCount > 0 ? 'has-alerts' : ''}`}
                onClick={toggle}
                aria-expanded={isOpen}
                aria-label={unseenAlertCount > 0 ? `Notifications, ${unseenAlertCount} unseen tracking alerts` : 'Notifications'}
            >
                {unseenAlertCount > 0 ? <TriangleAlert size={15} className="notifications-alert-icon" /> : <Bell size={15} />}
                <span>Notifications</span>
                {unseenAlertCount > 0 && <span className="notifications-alert-badge">{unseenAlertCount}</span>}
                {unseenAlertCount === 0 && unseenCount > 0 && <span className="notifications-badge">{unseenCount}</span>}
            </button>
            {isOpen && (
                <div className="notifications-panel" role="dialog" aria-label="Notifications">
                    {hasAlerts && (
                        <>
                            <div className="notifications-panel-header notifications-panel-header-alert">Tracking alerts</div>
                            <div className="notifications-list">
                                {stoppedAlerts.map((alert) => {
                                    const lastClick = formatLastClick(alert.lastEventAt);
                                    return (
                                        <article key={stoppedKey(alert)} className="notifications-item notifications-alert-item">
                                            <div className="notifications-item-header">
                                                <h4>
                                                    <TriangleAlert size={14} className="notifications-alert-icon" />
                                                    Tracking for "{alert.offerName}" has stopped
                                                </h4>
                                            </div>
                                            <p className="notifications-description">
                                                No clicks have been recorded for 5 days. Check that the tracking code is still installed on your pages.
                                            </p>
                                            {lastClick && <p className="notifications-description">Last click: {lastClick}</p>}
                                        </article>
                                    );
                                })}
                                {alerts.map((alert) => {
                                    const date = formatAlertDate(alert.alertedAt);
                                    return (
                                        <article key={alert.offerName} className="notifications-item notifications-alert-item">
                                            <div className="notifications-item-header">
                                                <h4>
                                                    <TriangleAlert size={14} className="notifications-alert-icon" />
                                                    Tracking for "{alert.offerName}" is not being recorded
                                                </h4>
                                            </div>
                                            <p className="notifications-description">
                                                RevTrack received a click or conversion for this offer, but that offer does not exist in your account. Events using that name are not being saved.
                                            </p>
                                            {alert.pageUrl && (
                                                <p className="notifications-description">
                                                    Detected on{' '}
                                                    <a href={alert.pageUrl} target="_blank" rel="noopener noreferrer">{alert.pageUrl}</a>
                                                </p>
                                            )}
                                            {date && <p className="notifications-date">{date}</p>}
                                            <button
                                                type="button"
                                                className="notifications-delete"
                                                onClick={() => deleteAlert(alert.offerName)}
                                                disabled={deletingName === alert.offerName}
                                            >
                                                {deletingName === alert.offerName ? 'Deleting...' : 'Delete'}
                                            </button>
                                            {deleteError === alert.offerName && (
                                                <p className="notifications-delete-error">Could not delete this alert.</p>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        </>
                    )}
                    <div className="notifications-panel-header">RevTrack updates</div>
                    <div className="notifications-list">
                        {UPDATES.map((update) => (
                            <article key={`${update.id}-${update.date}`} className="notifications-item">
                                <div className="notifications-item-header">
                                    <h4>{update.title}</h4>
                                    {update.isNew && <span className="notifications-new-badge">New</span>}
                                </div>
                                <p className="notifications-description">{update.description}</p>
                                <p className="notifications-date">{update.date}</p>
                            </article>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notifications;
